import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useApiClient } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";

interface ResetPasswordSearch {
  token?: string;
}

export const Route = createFileRoute("/auth/reset-password")({
  validateSearch: (search: Record<string, unknown>): ResetPasswordSearch => ({
    token:
      typeof search.token === "string" && /^[a-f0-9]{64}$/.test(search.token)
        ? search.token
        : undefined,
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { t } = useTranslation(["auth", "common"]);
  const api = useApiClient();
  const { token } = Route.useSearch();
  const { apiError: showApiError } = useToast();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Attempt deep link to mobile app
  useEffect(() => {
    if (token) {
      const deepLink = `njbsoft-field://reset-password?token=${encodeURIComponent(token)}`;
      const timeout = setTimeout(() => {
        // After a brief delay, the web form is already visible as fallback
      }, 1000);
      window.location.href = deepLink;
      return () => clearTimeout(timeout);
    }
  }, [token]);

  const resetMutation = useMutation({
    mutationFn: async (body: { token: string; new_password: string }) => {
      const { error } = await api.POST("/auth/reset-password", {
        body,
      });
      if (error) throw error;
    },
    onError: (err: unknown) => {
      const code = (err as { code?: string })?.code;
      showApiError(code);
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    const newPassword = formData.get("new_password") as string;
    const confirmPassword = formData.get("confirm_password") as string;

    const errors: Record<string, string> = {};
    if (!newPassword) {
      errors.new_password = t("validation.required");
    } else if (newPassword.length < 8) {
      errors.new_password = t("validation.minLength", { min: 8 });
    }
    if (!confirmPassword) {
      errors.confirm_password = t("validation.required");
    } else if (newPassword && confirmPassword !== newPassword) {
      errors.confirm_password = t("passwordMismatch");
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});

    if (!token) return;

    resetMutation.mutate({ token, new_password: newPassword });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface">
      <div className="w-full max-w-md space-y-8 rounded-lg bg-white p-8 shadow-lg dark:bg-gray-800">
        <div>
          <h2 className="text-center text-3xl font-bold text-on-surface">
            {t("resetPasswordTitle")}
          </h2>
          <p className="mt-2 text-center text-sm text-muted">{t("resetPasswordSubtitle")}</p>
        </div>

        {resetMutation.isSuccess ? (
          <div className="space-y-4">
            <div className="rounded-md bg-green-50 p-4 text-sm text-green-700 dark:bg-green-900/30 dark:text-green-400">
              {t("passwordResetSuccess")}
            </div>
            <Link
              to="/login"
              className="block text-center text-sm font-medium text-primary hover:text-primary-hover"
            >
              {t("backToLogin")}
            </Link>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit} noValidate>
            <div className="space-y-4">
              <div>
                <label htmlFor="new_password" className="block text-sm font-medium text-muted">
                  {t("newPasswordLabel")}
                </label>
                <input
                  id="new_password"
                  name="new_password"
                  type="password"
                  autoComplete="new-password"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-700 dark:text-on-surface"
                />
                {fieldErrors.new_password && (
                  <p className="mt-1 text-xs text-danger">{fieldErrors.new_password}</p>
                )}
              </div>
              <div>
                <label htmlFor="confirm_password" className="block text-sm font-medium text-muted">
                  {t("confirmPasswordLabel")}
                </label>
                <input
                  id="confirm_password"
                  name="confirm_password"
                  type="password"
                  autoComplete="new-password"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-700 dark:text-on-surface"
                />
                {fieldErrors.confirm_password && (
                  <p className="mt-1 text-xs text-danger">{fieldErrors.confirm_password}</p>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={resetMutation.isPending || !token}
              className="w-full rounded-md bg-primary px-4 py-2 text-on-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50"
            >
              {resetMutation.isPending ? t("resettingPassword") : t("resetPassword")}
            </button>

            <Link
              to="/login"
              className="block text-center text-sm font-medium text-primary hover:text-primary-hover"
            >
              {t("backToLogin")}
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
